import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Radio, Loader2, AlertTriangle, Video } from "lucide-react";
import api from "@/lib/api";
import HlsPlayer from "@/components/HlsPlayer";
import { LiveChat } from "@/components/LiveChat";
import { ShareButtons } from "@/components/ShareButtons";

// Hosted live embed page (iframe). Plays the live HLS feed and sends a
// heartbeat every 15s so every viewer here is counted toward the owner's
// total live viewer count on the dashboard. The count itself is NOT shown here.
// Optional ?accent=RRGGBB&text=RRGGBB query params let site owners match the
// top bar to their website colors.
export default function LiveEmbed() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [now, setNow] = useState(Date.now());
  const beatRef = useRef(null);
  const viewLoggedRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get(`/public/streams/${id}/live`);
      setS(r.data);
    } catch (e) {
      setError(e?.response?.status === 403 ? "paused" : true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Re-check live state periodically so the player appears when the broadcast starts.
    const poll = setInterval(load, 20000);
    return () => clearInterval(poll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const isLive = s?.is_live && !!s?.hls_playback_url;

  // Tick every second to drive the offline countdown.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Heartbeat: only while actually watching a live feed.
  useEffect(() => {
    if (!isLive) return;
    let sid = localStorage.getItem("sw_viewer_sid");
    if (!sid) {
      sid = crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2);
      localStorage.setItem("sw_viewer_sid", sid);
    }
    // Log a single play event per session when this live stream starts playing.
    if (viewLoggedRef.current !== id) {
      viewLoggedRef.current = id;
      api.post(`/analytics/view`, { stream_id: id, kind: "stream", session_id: sid, source: params.get("ref") || null }).catch(() => {});
    }
    let stop = false;
    const beat = async () => {
      try { await api.post(`/streams/${id}/heartbeat`, { session_id: sid }); } catch (e) { /* transient */ }
    };
    beat();
    beatRef.current = setInterval(beat, 15000);
    return () => { stop = true; clearInterval(beatRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isLive]);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <Loader2 className="h-6 w-6 text-amber-400 animate-spin" />
      </div>
    );
  }

  if (error || !s) {
    return (
      <div className="fixed inset-0 bg-black flex flex-col items-center justify-center text-center px-6">
        <AlertTriangle className="h-8 w-8 text-amber-400" />
        <p data-testid="embed-unavailable" className="mt-3 text-white font-medium">{error === "paused" ? "Temporarily unavailable" : "Stream unavailable"}</p>
        <p className="text-gray-500 text-sm mt-1">{error === "paused" ? "This church's broadcasts are paused right now. Please check back soon." : "This stream may have been removed."}</p>
      </div>
    );
  }

  const org = s.org || {};
  const qAccent = params.get("accent");
  const qText = params.get("text");
  const accent = qAccent ? `#${qAccent.replace("#", "")}` : (org.accent_color || "#F59E0B");
  const barText = qText ? `#${qText.replace("#", "")}` : "#0B0F17";
  const logo = org.logo_url || "";
  const poster = s.thumbnail_url || "";
  const showChat = !!s.chat_enabled && params.get("chat") !== "0";

  // Countdown to the next service (only when set and still in the future).
  const SOON_MS = 5 * 60 * 1000; // within 5 min -> "Starting soon…"
  const label = s.next_service_label || "";
  const nextAt = s.next_service_at ? new Date(s.next_service_at).getTime() : 0;
  const msLeft = nextAt ? nextAt - now : 0;
  const showCountdown = nextAt > 0 && msLeft > 0;
  const startingSoon = showCountdown && msLeft <= SOON_MS;
  const fmtCountdown = (ms) => {
    const t = Math.max(0, Math.floor(ms / 1000));
    const d = Math.floor(t / 86400), h = Math.floor((t % 86400) / 3600), m = Math.floor((t % 3600) / 60), sec = t % 60;
    const pad = (n) => String(n).padStart(2, "0");
    return d > 0 ? `${d}d ${pad(h)}:${pad(m)}:${pad(sec)}` : `${pad(h)}:${pad(m)}:${pad(sec)}`;
  };
  const fmtWhen = (iso) => {
    try { return new Date(iso).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
    catch { return ""; }
  };

  const OfflineOverlay = () => (
    <div className="absolute bottom-0 left-0 right-0 p-5 flex flex-col items-center gap-3 text-center">
      {startingSoon ? (
        <div data-testid="live-embed-countdown" className="rounded-2xl bg-black/65 backdrop-blur px-5 py-3.5 border border-white/10">
          <p className="font-display text-xl sm:text-2xl font-bold flex items-center gap-2 justify-center" style={{ color: accent }}>
            <span className="h-2 w-2 rounded-full animate-pulse" style={{ backgroundColor: accent }} /> Starting soon…
          </p>
          <p className="mt-1 text-xs text-white/80">{label ? `${label} · ` : ""}{fmtWhen(s.next_service_at)}</p>
        </div>
      ) : showCountdown ? (
        <div data-testid="live-embed-countdown" className="rounded-2xl bg-black/65 backdrop-blur px-5 py-3.5 border border-white/10">
          <p className="text-[11px] uppercase tracking-wider text-white/70">{label ? `${label} starts in` : "Next service starts in"}</p>
          <p className="mt-1 font-display text-2xl sm:text-3xl font-bold tabular-nums" style={{ color: accent }}>{fmtCountdown(msLeft)}</p>
          <p className="mt-1 text-xs text-white/80">{fmtWhen(s.next_service_at)}</p>
        </div>
      ) : (
        <span className="inline-flex items-center gap-2 rounded-full bg-black/60 backdrop-blur px-3 py-1.5 text-xs font-medium text-white">
          <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: accent }} />
          The broadcast is offline — starts automatically when live
        </span>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black flex flex-col select-none" data-testid="live-embed">
      {/* Branding bar — shows when there's a logo, an org name, or a custom color */}
      {(logo || org.organization || qAccent) ? (
        <div className="flex items-center gap-2 px-4 py-2 text-sm font-semibold" style={{ background: accent, color: barText }} data-testid="live-embed-bar">
          {logo ? <img src={logo} alt="logo" className="h-5 object-contain" /> : <Radio className="h-4 w-4" />} {org.organization || s.title}
        </div>
      ) : null}

      <div className="flex-1 min-h-0 flex flex-col min-[600px]:flex-row">
      <div className={"relative flex items-center justify-center overflow-hidden " + (showChat ? "aspect-video min-[600px]:aspect-auto min-[600px]:flex-1 shrink-0" : "flex-1")}>
        <ShareButtons settings={s.share} target={{ streamId: id }} title={org.organization ? `${s.title} — ${org.organization}` : s.title}
          className="absolute top-3 right-3 z-20 rounded-full bg-black/45 backdrop-blur px-2 py-1" />
        {isLive ? (
          <>
            <span className="absolute top-3 left-3 z-10 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-red-600 text-white pointer-events-none">
              <span className="h-1.5 w-1.5 rounded-full bg-white animate-live-dot" /> LIVE
            </span>
            <HlsPlayer src={s.hls_playback_url} poster={poster} autoPlay controls muted live className="h-full w-full" />
          </>
        ) : poster ? (
          // Offline: show the stream's poster image (set on the Stream page).
          <div className="absolute inset-0" data-testid="live-embed-offline">
            <img src={poster} alt={s.title || "Offline"} className="absolute inset-0 h-full w-full object-cover" data-testid="live-embed-offline-image" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-black/40" />
            <OfflineOverlay />
          </div>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6" data-testid="live-embed-offline">
            <div className="h-16 w-16 rounded-full border flex items-center justify-center mx-auto animate-pulse" style={{ borderColor: accent + "66" }}>
              <Video className="h-7 w-7" style={{ color: accent }} />
            </div>
            <p className="mt-4 text-white font-medium">The broadcast is offline</p>
            {startingSoon ? (
              <div data-testid="live-embed-countdown" className="mt-3">
                <p className="font-display text-xl sm:text-2xl font-bold flex items-center gap-2 justify-center" style={{ color: accent }}>
                  <span className="h-2 w-2 rounded-full animate-pulse" style={{ backgroundColor: accent }} /> Starting soon…
                </p>
                <p className="mt-1 text-xs text-gray-400">{label ? `${label} · ` : ""}{fmtWhen(s.next_service_at)}</p>
              </div>
            ) : showCountdown ? (
              <div data-testid="live-embed-countdown" className="mt-3">
                <p className="font-display text-2xl sm:text-3xl font-bold tabular-nums" style={{ color: accent }}>{fmtCountdown(msLeft)}</p>
                <p className="mt-1 text-xs text-gray-400">{label ? `${label} · ` : "Next service · "}{fmtWhen(s.next_service_at)}</p>
              </div>
            ) : (
              <p className="text-gray-500 text-sm mt-1">This page will start playing automatically when the stream goes live.</p>
            )}
          </div>
        )}
      </div>
      {showChat && <LiveChat streamId={id} accent={accent} />}
      </div>
    </div>
  );
}
